/**
 * InfoCardDetail - Box-based detail view component
 *
 * Architecture:
 * - Component provides building blocks (boxes)
 * - Page configures which boxes exist, their order, and visibility
 *
 * Box Types:
 * - Static box: fields only
 * - Image box: fields + expandable image (tap to reveal)
 * - Action box: fields + action buttons
 * - Any combination of above
 */

import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, Copy, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

export interface InfoField {
  /** Field label */
  label: string;
  /** Field value (will be converted to string for display/copy) */
  value: string | number | boolean | null | undefined;
  /** Optional formatting hint */
  format?: 'currency' | 'date' | 'status' | 'default';
  /** Whether this field can be copied */
  copyable?: boolean;
}

export interface ActionButton {
  /** Button label */
  label: string;
  /** Button action */
  onClick: () => void;
  /** Button variant */
  variant?: 'primary' | 'success' | 'danger' | 'warning' | 'secondary' | 'muted';
  /** Loading state */
  loading?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

export interface BoxImage {
  /** Image URL (can be provided directly or loaded lazily) */
  url?: string;
  /** Label for the image section (default: "Photo") */
  label?: string;
  /** Async loader for image URL (called when user expands) */
  onLoad?: () => Promise<string>;
}

export interface DetailBox {
  /** Unique identifier for the box */
  id: string;
  /** Optional box title */
  title?: string;
  /** Fields to display in this box */
  fields: InfoField[];
  /** Optional expandable image */
  image?: BoxImage;
  /** Optional action buttons for this box */
  actions?: ActionButton[];
  /** Whether this box should be rendered (page controls based on data) */
  visible: boolean;
}

export interface InfoCardDetailProps {
  /** Title displayed at top */
  title: string;
  /** Subtitle (optional, e.g., status text) */
  subtitle?: string;
  /** Status for coloring subtitle (includes both deposit and withdrawal statuses) */
  status?: 'pending' | 'paid' | 'completed' | 'failed' | 'cancelled' | 'declined' | 'banned' | 'active';
  /** Array of boxes to render (only visible boxes with non-null fields shown) */
  boxes: DetailBox[];
  /** Close handler */
  onClose?: () => void;
  /** Loading state for entire detail */
  isLoading?: boolean;
}

// ============================================
// HELPER COMPONENTS
// ============================================

/**
 * CopyableField - Tappable field that shows copy option
 */
interface CopyableFieldProps {
  label: string;
  value: string;
  format?: 'currency' | 'date' | 'status' | 'default';
  copyable?: boolean;
}

function CopyableField({ label, value, format = 'default', copyable = true }: CopyableFieldProps) {
  const [showCopy, setShowCopy] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowCopy(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [value]);

  const handleTap = () => {
    if (!value || value === '-' || !copyable) return;
    setShowCopy(true);
  };

  const formatValue = (val: string) => {
    switch (format) {
      case 'currency':
        return `${val} AZN`;
      case 'date':
        return val; // Already formatted by parent
      default:
        return val;
    }
  };

  const getStatusColor = (val: string) => {
    const lower = val.toLowerCase();
    if (lower === 'completed' || lower === 'active' || lower === 'success') {
      return 'text-emerald-600 bg-emerald-50';
    }
    if (lower === 'pending') {
      return 'text-amber-600 bg-amber-50';
    }
    if (lower === 'cancelled' || lower === 'banned' || lower === 'failed') {
      return 'text-rose-600 bg-rose-50';
    }
    return 'text-slate-600 bg-slate-50';
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleTap}
        className={cn(
          'w-full flex items-center justify-between py-3 px-1',
          'border-b border-slate-100 last:border-b-0',
          'transition-colors',
          value && value !== '-' && copyable && 'active:bg-slate-50',
        )}
      >
        <span className="text-sm text-slate-500">{label}</span>
        {format === 'status' ? (
          <span className={cn(
            'text-sm font-medium px-2 py-0.5 rounded-full',
            getStatusColor(value),
          )}>
            {value}
          </span>
        ) : (
          <span className="text-sm font-medium text-slate-800 text-right max-w-[60%] truncate">
            {formatValue(value) || '-'}
          </span>
        )}
      </button>

      {/* Copy popup */}
      {showCopy && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full',
              'text-sm font-medium',
              'shadow-lg',
              'transition-all duration-200',
              copied
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-800 text-white hover:bg-slate-700',
            )}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </>
            )}
          </button>
          {!copied && (
            <button
              type="button"
              onClick={() => setShowCopy(false)}
              className="p-1 rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ExpandableImage - Collapsible image section within a box
 */
interface ExpandableImageProps {
  image: BoxImage;
}

function ExpandableImage({ image }: ExpandableImageProps) {
  const [expanded, setExpanded] = useState(false);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(image.url || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    // If we need to load the image
    if (!loadedUrl && image.onLoad) {
      setLoading(true);
      setError(false);
      try {
        const url = await image.onLoad();
        setLoadedUrl(url);
        setExpanded(true);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    } else if (loadedUrl) {
      setExpanded(true);
    }
  };

  // Don't render if no URL and no loader
  if (!image.url && !image.onLoad) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={handleExpand}
        className={cn(
          'w-full flex items-center justify-between',
          'px-4 py-3 rounded-xl',
          'bg-slate-50',
          'border border-slate-200',
          'transition-all duration-200',
          'hover:border-slate-300',
        )}
      >
        <span className="text-sm font-medium text-slate-700">
          {loading ? 'Loading...' : (image.label || 'Photo')}
        </span>
        {loading ? (
          <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
        ) : expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {/* Expanded image */}
      {expanded && loadedUrl && (
        <div className={cn(
          'mt-2 rounded-xl overflow-hidden',
          'border border-slate-200',
        )}>
          <img
            src={loadedUrl}
            alt={image.label || 'Photo'}
            className="w-full h-auto"
            onError={() => setError(true)}
          />
        </div>
      )}

      {error && (
        <div className="mt-2 px-4 py-3 rounded-xl bg-rose-50 text-rose-600 text-sm">
          Failed to load image
        </div>
      )}
    </div>
  );
}

/**
 * BoxActions - Action buttons within a box
 */
interface BoxActionsProps {
  actions: ActionButton[];
}

function BoxActions({ actions }: BoxActionsProps) {
  if (actions.length === 0) return null;

  const getButtonStyles = (variant: ActionButton['variant'] = 'primary') => {
    switch (variant) {
      case 'success':
        return { className: 'text-white hover:opacity-90 active:opacity-80', style: { backgroundColor: '#10b981' } };
      case 'danger':
        return { className: 'text-white hover:opacity-90 active:opacity-80', style: { backgroundColor: '#f43f5e' } };
      case 'warning':
        return { className: 'text-black font-bold hover:opacity-90 active:opacity-80', style: { backgroundColor: '#f59e0b' } };
      case 'secondary':
        return { className: 'text-slate-700 hover:opacity-90 active:opacity-80', style: { backgroundColor: '#e2e8f0' } };
      case 'muted':
        return { className: 'text-white hover:opacity-90 active:opacity-80', style: { backgroundColor: '#94a3b8' } };
      case 'primary':
      default:
        return { className: 'text-white hover:opacity-90 active:opacity-80', style: { backgroundColor: '#0ea5e9' } };
    }
  };

  return (
    <div className={cn(
      'flex gap-2 mt-3',
      actions.length === 1 ? 'flex-col' : 'flex-row',
    )}>
      {actions.map((action, index) => {
        const buttonStyles = getButtonStyles(action.variant);
        return (
          <button
            key={index}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled || action.loading}
            style={buttonStyles.style}
            className={cn(
              'flex-1 flex items-center justify-center',
              'px-4 py-3 rounded-full',
              'text-sm font-semibold',
              'transition-all duration-200',
              'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
              buttonStyles.className,
              (action.disabled || action.loading) && 'opacity-50 cursor-not-allowed',
            )}
          >
            {action.loading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              action.label
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * DetailBoxComponent - Renders a single box with fields, optional image, optional actions
 */
interface DetailBoxComponentProps {
  box: DetailBox;
}

function DetailBoxComponent({ box }: DetailBoxComponentProps) {
  // Filter out fields with null/undefined/empty values
  const validFields = box.fields.filter(f =>
    f.value !== null && f.value !== undefined && f.value !== ''
  );

  // If all fields are empty and no image/actions, don't render the box
  if (validFields.length === 0 && !box.image && (!box.actions || box.actions.length === 0)) {
    return null;
  }

  return (
    <div className="mb-4 last:mb-0">
      {/* Box Title */}
      {box.title && (
        <h3 className="text-sm font-semibold text-slate-600 mb-2 px-1">
          {box.title}
        </h3>
      )}

      {/* Fields Card */}
      {validFields.length > 0 && (
        <div className={cn(
          'rounded-2xl bg-white',
          'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
          'border border-slate-200',
          'px-4',
        )}>
          {validFields.map((field, index) => (
            <CopyableField
              key={index}
              label={field.label}
              value={String(field.value ?? '-')}
              format={field.format}
              copyable={field.copyable !== false}
            />
          ))}
        </div>
      )}

      {/* Expandable Image (only image is collapsible) */}
      {box.image && <ExpandableImage image={box.image} />}

      {/* Box Actions */}
      {box.actions && box.actions.length > 0 && (
        <BoxActions actions={box.actions} />
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function InfoCardDetail({
  title,
  subtitle,
  status,
  boxes,
  onClose,
  isLoading = false,
}: InfoCardDetailProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'completed':
      case 'active':
        return 'text-emerald-600 bg-emerald-50';
      case 'pending':
        return 'text-amber-600 bg-amber-50';
      case 'cancelled':
      case 'failed':
      case 'banned':
        return 'text-rose-600 bg-rose-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };

  // Filter to only visible boxes
  const visibleBoxes = boxes.filter(box => box.visible);

  if (isLoading) {
    return (
      <div className="p-4">
        {/* Header skeleton with functional X button */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="h-7 w-32 bg-slate-200 rounded-lg animate-pulse" />
            <div className="h-6 w-16 bg-slate-100 rounded-full animate-pulse mt-2" />
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'p-2 rounded-full',
                'text-slate-400 hover:text-slate-600',
                'hover:bg-slate-100',
                'transition-colors',
              )}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Box skeletons */}
        {[1, 2].map((i) => (
          <div key={i} className="mb-4">
            <div className="h-4 w-24 bg-slate-100 rounded animate-pulse mb-2" />
            <div className={cn(
              'rounded-2xl bg-white',
              'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
              'border border-slate-200',
              'px-4',
            )}>
              {[1, 2, 3].map((j) => (
                <div
                  key={j}
                  className="flex items-center justify-between py-3 px-1 border-b border-slate-100 last:border-b-0"
                >
                  <div className="h-4 w-24 bg-slate-100 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Action buttons skeleton */}
        <div className="flex gap-2 mt-4">
          <div className="flex-1 h-12 bg-slate-200 rounded-full animate-pulse" />
          <div className="flex-1 h-12 bg-slate-200 rounded-full animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{title}</h2>
          {subtitle && (
            <span className={cn(
              'inline-block mt-1 px-2.5 py-1 rounded-full',
              'text-xs font-semibold',
              getStatusColor(),
            )}>
              {subtitle}
            </span>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'p-2 rounded-full',
              'text-slate-400 hover:text-slate-600',
              'hover:bg-slate-100',
              'transition-colors',
            )}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Render visible boxes */}
      {visibleBoxes.map((box) => (
        <DetailBoxComponent key={box.id} box={box} />
      ))}

      {/* Empty state if no visible boxes */}
      {visibleBoxes.length === 0 && (
        <div className="text-center py-8 text-slate-400">
          No information available
        </div>
      )}
    </div>
  );
}

// ============================================
// RE-EXPORTS FOR BACKWARDS COMPATIBILITY
// ============================================

// Types are already exported inline above
